import React, { PropsWithChildren } from 'react';

import { theme } from '@sg/garnish';

import { AppContent } from './App.content';
import {
  ForceUpdateMode,
  LoadingAnimation,
  LocationPermissionPrimer,
  MaintenanceMode,
  useIsForceUpdateModeEnabled,
  useIsMaintenanceModeEnabled,
  useLocationPermissionPrimer,
} from './components';
import { use3rdPartyIntegrations } from './hooks';

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Renders the post-gate render branches: force-update mode, maintenance mode,
 * the location permission primer flow, or the regular app content. The
 * launch-time gate (and the in-flight restart loading state) is owned by
 * `LaunchGate` upstream.
 */
export const App = (props: PropsWithChildren) => {
  const { children } = props;

  const isMaintenanceModeEnabled = useIsMaintenanceModeEnabled();
  const isForceUpdatedModeEnabled = useIsForceUpdateModeEnabled();
  const locationPermissionPrimer = useLocationPermissionPrimer({ variants: LOCATION_PRIMER_APP_BOOT_VARIANTS }); // prettier-ignore

  use3rdPartyIntegrations();

  // ─────────────────────────────────────────────────────────────────────

  const { isLocationPermissionsLoading, isPrimerActive } = locationPermissionPrimer;

  // ─────────────────────────────────────────────────────────────────────

  if (isForceUpdatedModeEnabled) return <ForceUpdateMode />;

  if (isMaintenanceModeEnabled) return <MaintenanceMode />;

  if (isLocationPermissionsLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  if (isPrimerActive) {
    return (
      <LocationPermissionPrimer
        isRequesting={locationPermissionPrimer.isRequestingLocationPermission}
        isSkipping={locationPermissionPrimer.isSkippingLocationPermission}
        onAllow={locationPermissionPrimer.onAllow}
        onSkip={locationPermissionPrimer.onSkip}
      />
    );
  }

  return <AppContent>{children}</AppContent>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_PRIMER_APP_BOOT_VARIANTS: LocationPrimerVariants = ['A'];

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationPrimerVariants = Parameters<typeof useLocationPermissionPrimer>[0]['variants'];
